'use client';

import React from 'react';

/**
 * Input mode types for different visual styles
 */
export type InputMode = 'primary' | 'secondary' | 'danger';

/**
 * Input component properties
 */
export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  mode?: InputMode;
  fullWidth?: boolean;
  glow?: boolean;
}

/**
 * Neon-themed input component with glow effects
 * @param {InputProps} props - Input properties
 * @returns {JSX.Element} Styled input with neon border and glow
 */
export const Input: React.FC<InputProps> = ({
  className = '',
  mode = 'primary',
  fullWidth = false,
  glow = false,
  ...props
}) => {
  const baseClasses = 'bg-black border-2 rounded-md px-4 py-2 transition-all duration-300 focus:outline-none focus:ring-2';

  const modeClasses = {
    primary: 'focus:border-cyan-400 border-cyan-400 text-cyan-400 focus:ring-cyan-400 placeholder-cyan-400/50 focus:neon-glow-primary',
    secondary: 'focus:border-fuchsia-400 border-fuchsia-400 text-fuchsia-400 focus:ring-fuchsia-400 placeholder-fuchsia-400/50 focus:neon-glow-secondary',
    danger: 'focus:border-danger border-danger text-danger focus:ring-danger placeholder-danger/50 focus:neon-glow-danger',
  } as const;

  const widthClasses = fullWidth ? 'w-full' : '';

  const classes = `${baseClasses} ${modeClasses[mode]} ${widthClasses} ${className}`.trim();

  return <input x-component-name="Input" className={classes} {...props} />;
};